import {
  Card,
  CardBody,
  HStack,
  VStack,
  Text,
  Badge,
  Button,
  Icon,
  Box,
  Tooltip,
  Progress,
  useColorModeValue,
  useDisclosure,
} from '@chakra-ui/react';
import {
  MdCheckCircle,
  MdLock,
  MdPending,
  MdEdit,
  MdPlayArrow,
  MdHistory,
  MdSync,
  MdSchedule,
  MdWarning,
  MdStickyNote2,
  MdImage,
  MdVideocam,
  MdAudioFile,
  MdDescription,
  MdAttachFile,
  MdUpdate,
} from 'react-icons/md';
import DOMPurify from 'dompurify';
import { ReactNode, useMemo } from 'react';
import type { TaskNote, BotTag } from '@/lib/sdk/chatbot/types';
import { NotesDialog } from './NotesDialog';
import { DocumentsDialog } from './DocumentsDialog';

function formatRelativeTime(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
}

export type TaskCardStatus = 'completed' | 'blocked' | 'pending' | 'wip' | 'due_soon' | 'soft_overdue';

export interface TaskCardV2Props {
  title: string;
  number?: number;
  status: TaskCardStatus;
  displayValue?: string;
  progress?: number;
  editCount?: number;
  actionLabel?: string;
  actionTooltip?: string;
  actionIcon?: ReactNode;
  onClick?: () => void;
  onActionClick?: () => void;
  isDisabled?: boolean;
  softDueDate?: string | null;
  userName?: string;
  updatedAt?: string;
  notes?: TaskNote[];
  description?: string;
  documentLinks?: string[];
  tags?: BotTag[];
}

export function TaskCardV2({
  title,
  number,
  status,
  displayValue,
  progress,
  editCount,
  actionLabel,
  actionTooltip,
  actionIcon,
  onClick,
  onActionClick,
  isDisabled = false,
  softDueDate,
  userName,
  updatedAt,
  notes = [],
  description,
  documentLinks = [],
  tags = [],
}: TaskCardV2Props) {
  const notesDialog = useDisclosure();
  const docsDialog = useDisclosure();

  const isCompleted = status === 'completed';
  const isBlocked = status === 'blocked';
  const isWip = status === 'wip';
  const isDueSoon = status === 'due_soon';
  const isSoftOverdue = status === 'soft_overdue';

  // Background colors
  const completedBg = useColorModeValue('green.50', 'green.900');
  const pendingBg = useColorModeValue('gray.50', 'gray.700');
  const blockedBg = useColorModeValue('red.50', 'red.900');
  const wipBg = useColorModeValue('orange.50', 'orange.900');
  const dueSoonBg = useColorModeValue('yellow.50', 'yellow.900');
  const softOverdueBg = useColorModeValue('red.50', 'red.900');

  const bgColor = isBlocked ? blockedBg
    : isCompleted ? completedBg
    : isWip ? wipBg
    : isSoftOverdue ? softOverdueBg
    : isDueSoon ? dueSoonBg
    : pendingBg;

  const borderColor = isCompleted ? 'green.200'
    : isBlocked ? 'red.200'
    : isWip ? 'orange.200'
    : isSoftOverdue ? 'red.300'
    : isDueSoon ? 'yellow.200'
    : 'border-default';

  // Status icon
  const StatusIcon = isCompleted ? MdCheckCircle
    : isBlocked ? MdLock
    : isWip ? MdSync
    : isSoftOverdue ? MdWarning
    : isDueSoon ? MdSchedule
    : MdPending;
  const statusIconColor = isCompleted ? 'green.500'
    : isBlocked ? 'red.400'
    : isWip ? 'orange.500'
    : isSoftOverdue ? 'red.500'
    : isDueSoon ? 'yellow.500'
    : 'gray.400';

  // Status badge
  const statusLabel = isCompleted ? 'Completed'
    : isBlocked ? 'Blocked'
    : isWip ? 'In Progress'
    : isSoftOverdue ? 'Overdue'
    : isDueSoon ? 'Due Soon'
    : 'Pending';
  const statusColorScheme = isCompleted ? 'green'
    : isBlocked ? 'red'
    : isWip ? 'orange'
    : isSoftOverdue ? 'red'
    : isDueSoon ? 'yellow'
    : 'gray';

  // Action button defaults
  const defaultActionLabel = isCompleted ? 'Edit' : isWip ? 'Update' : 'Process';
  const defaultActionIcon = isCompleted ? <MdEdit /> : isWip ? <MdEdit /> : <MdPlayArrow />;
  const defaultActionTooltip = isBlocked
    ? 'Waiting for approval'
    : isCompleted ? 'Edit response'
    : isWip ? 'Add notes or mark complete'
    : 'Enter data';

  // Deadline line
  const deadlineLine = useMemo(() => {
    if (!softDueDate || isCompleted) return null;
    const softDue = new Date(softDueDate);
    const now = new Date();
    const diffMs = softDue.getTime() - now.getTime();
    if (diffMs < 0) {
      return { text: `Deadline passed ${softDue.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}`, icon: '🔴', color: 'red.500' };
    }
    if (diffMs <= 24 * 60 * 60 * 1000) {
      return { text: 'Due today', icon: '⏰', color: 'yellow.600' };
    }
    return { text: `Due ${softDue.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}`, icon: '⚠️', color: 'orange.500' };
  }, [softDueDate, isCompleted]);

  // Filtered user notes
  const userNotes = useMemo(() =>
    notes.filter(n => !n.is_action && !n.is_custom_data)
      .sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()),
    [notes]
  );

  const latestNote = userNotes[0] ?? null;

  // Media indicator icon for latest note preview
  const getMediaIcon = (mimeType: string | undefined) => {
    const mt = mimeType?.toLowerCase() || '';
    if (mt.startsWith('image/')) return MdImage;
    if (mt.startsWith('video/')) return MdVideocam;
    if (mt.startsWith('audio/')) return MdAudioFile;
    return MdDescription;
  };

  const handleCardClick = () => {
    if (!isDisabled && !isBlocked && onClick) {
      onClick();
    }
  };

  const handleActionClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onActionClick) {
      onActionClick();
    } else if (onClick) {
      onClick();
    }
  };

  const handleNotesClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    notesDialog.onOpen();
  };

  const handleDocsClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    docsDialog.onOpen();
  };

  // Body content: deadline → latest note → displayValue → description
  const hasBody = deadlineLine || latestNote || displayValue || description;

  return (
    <>
      <Card
        bg={bgColor}
        borderWidth="1px"
        borderColor={borderColor}
        opacity={isBlocked || isDisabled ? 0.7 : 1}
        cursor={isBlocked || isDisabled ? 'not-allowed' : 'pointer'}
        _hover={
          !isBlocked && !isDisabled
            ? { borderColor: 'brand.500', transform: 'translateY(-1px)' }
            : {}
        }
        transition="all 0.2s"
        onClick={handleCardClick}
      >
        <CardBody py={3} px={4}>
          <VStack spacing={2} align="stretch">
            {/* HEADER: status icon + title (65% max) + badge right */}
            <HStack spacing={3} align="start" justify="space-between">
              <HStack spacing={2} align="start" maxW="65%" flex={1} minW={0}>
                <Box flexShrink={0} pt={0.5}>
                  <Icon as={StatusIcon} color={statusIconColor} boxSize={5} />
                </Box>
                <Text
                  fontWeight="semibold"
                  color="text-primary"
                  fontSize={{ base: 'sm', md: 'md' }}
                  wordBreak="break-word"
                >
                  {number !== undefined ? `${number}. ` : ''}
                  {title}
                </Text>
              </HStack>
              <HStack spacing={2} flexShrink={0}>
                <Badge colorScheme={statusColorScheme} fontSize="xs">
                  {statusLabel}
                </Badge>
                {editCount !== undefined && editCount > 0 && (
                  <Tooltip label={`Edited ${editCount} time${editCount > 1 ? 's' : ''}`}>
                    <Badge
                      colorScheme="orange"
                      fontSize="xs"
                      display="flex"
                      alignItems="center"
                      gap={1}
                    >
                      <Icon as={MdHistory} boxSize={3} />
                      {editCount}
                    </Badge>
                  </Tooltip>
                )}
              </HStack>
            </HStack>

            {/* BODY: description (always), deadline, latest note/displayValue */}
            {hasBody && (
              <Box pl={7}>
                {description && (
                  <Text fontSize="xs" color="text-secondary" fontStyle="italic" noOfLines={2} mb={1}>
                    {description}
                  </Text>
                )}
                {deadlineLine && (
                  <Text fontSize="xs" color={deadlineLine.color} fontWeight="medium">
                    {deadlineLine.icon} {deadlineLine.text}
                  </Text>
                )}
                {latestNote ? (
                  <Box mt={deadlineLine ? 1 : 0}>
                    {latestNote.status_text && (
                      <Box
                        fontSize="xs"
                        color="text-secondary"
                        // overflow="hidden"
                        whiteSpace="normal"
                        wordBreak="break-word"
                        textOverflow="ellipsis"
                        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(`Latest: \u201c${latestNote.status_text}\u201d`) }}
                      />
                    )}
                    {latestNote.media_url && (
                      <HStack spacing={1} mt={0.5}>
                        <Icon as={getMediaIcon(latestNote.mime_type)} color="text-muted" boxSize={3} />
                        <Text fontSize="xs" color="text-muted">
                          {latestNote.file_name || 'Attachment'}
                        </Text>
                      </HStack>
                    )}
                  </Box>
                ) : displayValue ? (
                  <Text fontSize="xs" color={isBlocked ? 'text-secondary' : 'green.600'} fontWeight="medium" noOfLines={1}>
                    {displayValue}
                  </Text>
                ) : null}
              </Box>
            )}

            {/* TAGS */}
            {tags.length > 0 && (
              <HStack spacing={1} pl={7} flexWrap="wrap">
                {tags.map((tag, i) => (
                  <Badge key={i} colorScheme="teal" fontSize="xs" variant="subtle">
                    {String(tag.value)}
                  </Badge>
                ))}
              </HStack>
            )}

            {/* FOOTER: user/timestamp left (65%) + notes icon + action button right */}
            <HStack justify="space-between" align="center" spacing={2}>
              <HStack spacing={1} maxW="65%" minW={0} flex={1}>
                {userName && (
                  <Text fontSize="xs" color="text-secondary" isTruncated>
                    👤 {userName}
                  </Text>
                )}
                {userName && updatedAt && (
                  <Text fontSize="xs" color="text-secondary">&middot;</Text>
                )}
                {updatedAt && (
                  <Tooltip
                    label={`Updated ${new Date(updatedAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })} ${new Date(updatedAt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`}
                    placement="top"
                  >
                    <HStack spacing={0.5} flexShrink={0}>
                      <Icon as={MdUpdate} color="text-secondary" boxSize={3} />
                      <Text fontSize="xs" color="text-secondary">
                        {formatRelativeTime(updatedAt)}
                      </Text>
                    </HStack>
                  </Tooltip>
                )}
              </HStack>

              <HStack spacing={2} flexShrink={0}>
                {userNotes.length > 0 && (
                  <Tooltip label={`${userNotes.length} note${userNotes.length > 1 ? 's' : ''}`}>
                    <Badge
                      as="button"
                      colorScheme="blue"
                      fontSize="xs"
                      display="flex"
                      alignItems="center"
                      gap={1}
                      cursor="pointer"
                      onClick={handleNotesClick}
                      _hover={{ opacity: 0.8 }}
                    >
                      <Icon as={MdStickyNote2} boxSize={3} />
                      {userNotes.length}
                    </Badge>
                  </Tooltip>
                )}
                {documentLinks.length > 0 && (
                  <Tooltip label={`${documentLinks.length} document${documentLinks.length > 1 ? 's' : ''}`}>
                    <Badge
                      as="button"
                      colorScheme="purple"
                      fontSize="xs"
                      display="flex"
                      alignItems="center"
                      gap={1}
                      cursor="pointer"
                      onClick={handleDocsClick}
                      _hover={{ opacity: 0.8 }}
                    >
                      <Icon as={MdAttachFile} boxSize={3} />
                      {documentLinks.length}
                    </Badge>
                  </Tooltip>
                )}
                <Tooltip label={actionTooltip || defaultActionTooltip}>
                  <Button
                    size="sm"
                    colorScheme={isCompleted ? 'gray' : isWip ? 'orange' : isSoftOverdue ? 'red' : isDueSoon ? 'yellow' : 'brand'}
                    variant={isCompleted || isWip ? 'outline' : isSoftOverdue ? 'solid' : isDueSoon ? 'outline' : 'solid'}
                    leftIcon={actionIcon || defaultActionIcon}
                    isDisabled={isBlocked || isDisabled}
                    onClick={handleActionClick}
                  >
                    {actionLabel || defaultActionLabel}
                  </Button>
                </Tooltip>
              </HStack>
            </HStack>

            {/* Optional progress bar */}
            {progress !== undefined && (
              <Progress
                value={progress}
                size="xs"
                colorScheme={isCompleted ? 'green' : 'brand'}
                borderRadius="full"
              />
            )}
          </VStack>
        </CardBody>
      </Card>

      {/* Notes dialog */}
      <NotesDialog
        isOpen={notesDialog.isOpen}
        onClose={notesDialog.onClose}
        notes={notes}
        taskName={title}
        description={description}
      />

      {/* Documents dialog */}
      <DocumentsDialog
        isOpen={docsDialog.isOpen}
        onClose={docsDialog.onClose}
        documentLinks={documentLinks}
        taskName={title}
      />
    </>
  );
}